import { useTransaction } from '@/OneLiners';
import {
    ActionStatusHelper,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    ActionResponse,
    useFormLifeCycle,
} from "@/Shared";

import { useTransactionEnricher } from "./useTransactionEnricher";
import { TransactionFormViewModel } from "../ViewModels/TransactionFormViewModel";
import { TransactionValidationSchema } from "../TransactionFormValidation";
import { TransactionModel } from "../TransactionModel";


/**
 * useTransactionForm hook – manages activity form data and interactions.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Initialises activity form data by fetching current activity and agent list
 * - Tracks form submission state and response (create or update)
 * - Exposes form logic to the component layer
 * 
 * Behavior:
 * - On mount, fetches the current activity (if editing) and the list of agents
 * - Automatically switches between `createTransaction` or `updateTransaction` based on activity presence
 * - Tracks the response of the latest action to provide submission feedback
 */
export const useTransactionForm = (props: DomainHookProps): DomainFormHook => {
    const { extraArgs, permissionHook } = props;
    const { mandate } = extraArgs ?? {};
    const { domainType } = DOMAIN_CONFIGS.Transaction;

    /**
     * Transaction domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const transactionHook = useTransaction();

    /**
     * Transaction-related enums exposed to the UI layer.
     * Used to build the form view model.
     */
    const { TransactionEnums, MandateEnums } = useAppEnum();

    /**
     * Transaction enrichment layer.
     * Provides additional data (users, computed fields, etc.)
     * required to build the form view model.
     */
    const {
        userMap,
        contactMap,
        enricherResponse,
        needMap,
        propertyMap,
        mandateMap,
        getEnrichedTransaction,
    } = useTransactionEnricher();


    /**
     * Aggregated response representing the global initialization state.
     * Combines:
     * - entity domain state
     * - permission loading state
     * - enrichment layer state
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            transactionHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: transactionHook.stateResponse.error ??
            enricherResponse.error
    };

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: transactionHook,
        buildVM: (currentEntity: TransactionModel) => {
            return new TransactionFormViewModel({
                viewEnums: TransactionEnums,
                mandateEnums: MandateEnums,
                contactMap,
                agentMap: userMap,
                mandateMap,
                needMap,
                propertyMap,
                enrichedTransaction: getEnrichedTransaction(currentEntity),
                mandate
            })
        },
    });
    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        viewEnums: TransactionEnums,
        ValidationSchema: TransactionValidationSchema(TransactionEnums),
    }
}